/**
 * 어드민 v2 메뉴 레지스트리 — 권한(권한 그룹 grants)의 단일 진실 소스 (FIKET-417).
 *
 * 서버 adminMiddleware(API 경로 → 메뉴 → 필요한 액션 판정), admin SideNav/라우트 가드(메뉴 필터),
 * 권한 관리 화면(메뉴 x 액션 매트릭스)이 전부 이 목록을 읽는다.
 *
 * - `apiScopes` 는 경로 prefix. `path === scope || path.startsWith(scope + '/')` 로 매칭한다.
 * - 한 API 를 여러 메뉴가 공유할 수 있다(예: /api/admin/dashboard/ads). 부여된 메뉴 중 하나라도 맞으면 허용.
 * - 레지스트리에 없는 경로는 MANAGER 에게 항상 거부(deny by default). ADMIN 은 이 목록과 무관하게 전권.
 * - `adminOnly` 메뉴는 그룹에 부여할 수 없다(권한 관리 자체 등).
 *
 * 새 v2 메뉴를 추가하면 여기에도 항목을 추가해야 MANAGER 가 볼 수 있다.
 */
export const ADMIN_MENU_ACTIONS = ['view', 'edit'];
export const ADMIN_MENU_ACTION_LABELS = {
    view: '보기',
    edit: '편집',
};
export const ADMIN_MENUS = [
    { key: 'announcements', label: '공지 관리', group: '운영', navPath: '/v2/announcements', apiScopes: ['/api/admin/announcements'] },
    {
        key: 'dashboard',
        label: '대시보드',
        group: '',
        navPath: '/v2/dashboard',
        apiScopes: ['/api/admin/dashboard/summary', '/api/admin/dashboard/v2', '/api/admin/dashboard/ads'],
    },
    {
        // 일별 지표 (FIKET-462) — 날짜 × 광고비 × 상품별 판매 × 프로모션 표 + 하루 상세 드로어.
        // /api/admin/dashboard/ads 는 "광고비 불러오기"(POST /ads/sync, edit) 때문에 포함한다.
        key: 'daily-metrics',
        label: '일별 지표',
        group: '',
        navPath: '/v2/daily-metrics',
        apiScopes: ['/api/admin/daily-metrics', '/api/admin/dashboard/ads'],
    },
    {
        key: 'users',
        label: '회원',
        group: '운영',
        navPath: '/v2/users',
        apiScopes: ['/api/admin/users', '/api/admin/dashboard/user-stats'],
    },
    {
        key: 'invitations',
        label: '청첩장',
        group: '운영',
        navPath: '/v2/invitations',
        apiScopes: ['/api/admin/invitations'],
    },
    {
        // 문의 상세의 회원 섹션(FIKET-387)이 회원 조회·크레딧 지급·토스 환불을 호출한다.
        // 답변 템플릿(FIKET-400)은 고객 문의 페이지의 탭이라 같은 메뉴 키로 묶는다(GET=view, 변이=edit).
        key: 'inquiries',
        label: '고객 문의',
        group: 'CS',
        navPath: '/v2/inquiries',
        apiScopes: [
            '/api/admin/contact-inquiries',
            '/api/admin/contact-reply-templates',
            '/api/admin/users',
            '/api/admin/payments',
            '/api/admin/toss/payments',
        ],
    },
    {
        // 챗봇 대화 로그(읽기 전용)·FAQ 관리·설정(edit 필요)·테스트 대화(/api/chatbot/* 는 authMiddleware 만). navPath prefix 로 전부 커버.
        key: 'chatbot',
        label: '챗봇',
        group: 'CS',
        navPath: '/v2/chatbot',
        apiScopes: ['/api/admin/chatbot-sessions', '/api/admin/chatbot-faqs', '/api/admin/chatbot-settings', '/api/admin/chatbot-tools'],
    },
    {
        key: 'ai-generations',
        label: 'AI 생성',
        group: 'AI',
        navPath: '/v2/ai-generations',
        apiScopes: ['/api/admin/ai-activity-history', '/api/admin/ai-activity-stats'],
    },
    {
        key: 'ai-ratings',
        label: 'AI 평가',
        group: 'AI',
        navPath: '/v2/ai-ratings',
        apiScopes: ['/api/admin/ratings'],
    },
    {
        key: 'prompt-blocks',
        label: '컨셉 블록',
        group: 'AI',
        navPath: '/v2/prompt-blocks',
        apiScopes: ['/api/admin/prompt-blocks', '/api/admin/ai-models', '/api/admin/presign-upload'],
    },
    {
        // AI 모델 레지스트리(ai_registered_models) — 사진 생성에 쓰는 이미지/텍스트 모델의 활성·추천·기본 지정, 등록·수정·삭제, 프로바이더 모델 탐색.
        // /api/admin/ai-models 는 컨셉 블록(카드 이미지 모델 선택, 읽기)과 공유한다.
        key: 'ai-models',
        label: 'AI 모델',
        group: 'AI',
        navPath: '/v2/ai-models',
        apiScopes: ['/api/admin/ai-models'],
    },
    {
        key: 'ai-usage-logs',
        label: 'AI 사용량 로그',
        group: 'AI',
        navPath: '/v2/ai-usage-logs',
        apiScopes: ['/api/admin/ai-usage-logs'],
        adminOnly: true,
    },
    {
        // 참조사진 품질(FIKET-441) — 업로드 검수 임계값(app_settings reference_photo_quality_thresholds)과 검수 히스토리·원본 열람.
        // 얼굴 원본(생체정보) 열람과 전역 app_settings 쓰기를 포함하므로 ADMIN 전용(v1 시절과 동일한 접근 범위 유지).
        key: 'ai-reference-quality',
        label: '참조사진 품질',
        group: 'AI',
        navPath: '/v2/ai-reference-quality',
        apiScopes: ['/api/admin/ai-reference-photos', '/api/admin/settings'],
        adminOnly: true,
    },
    {
        key: 'revenue',
        label: '매출 관리',
        group: '커머스',
        navPath: '/v2/revenue',
        apiScopes: ['/api/admin/revenue', '/api/admin/dashboard/series', '/api/admin/smartstore', '/api/admin/toss'],
    },
    {
        key: 'promotions',
        label: '프로모션',
        group: '커머스',
        navPath: '/v2/promotions',
        apiScopes: ['/api/admin/promotions'],
    },
    {
        // 스토어 링크 (FIKET-470) — 스마트스토어 상품 상세용 nid 진입 링크 생성(v1 /wedding/smartstore-links 이관).
        // 템플릿·감사장 디자인 목록은 미리보기 링크 카드 렌더용(GET 만 쓰므로 view 로 충분).
        key: 'smartstore-links',
        label: '스토어 링크',
        group: '커머스',
        navPath: '/v2/smartstore-links',
        apiScopes: ['/api/admin/smartstore-link', '/api/admin/templates', '/api/admin/thankyou-designs'],
    },
    {
        key: 'attribution',
        label: '유입·광고 분석',
        group: '마케팅',
        navPath: '/v2/attribution',
        apiScopes: ['/api/stats', '/api/analytics', '/api/marketing-links', '/api/admin/dashboard/ads'],
    },
    {
        key: 'ad-spend',
        label: '광고비 세부내역',
        group: '마케팅',
        navPath: '/v2/ad-spend',
        apiScopes: ['/api/admin/dashboard/ads'],
    },
    {
        // 경영지원실용 월별 리포트 (FIKET-436). 월 집계 API 1개 + 광고비·유입은 기존 일별 API를 화면에서 월 합산.
        // /api/admin/revenue/monthly 는 revenue 메뉴 scope(/api/admin/revenue)에도 걸리지만, 이 메뉴만 부여받아도 통과해야 한다.
        key: 'finance',
        label: '월별 리포트',
        group: '경영지원',
        navPath: '/v2/finance',
        apiScopes: ['/api/admin/revenue/monthly', '/api/admin/revenue/daily', '/api/admin/dashboard/series', '/api/admin/dashboard/ads'],
    },
    {
        // 환불률 페이지 (FIKET-511) — 결제일 코호트 환불률(일/주/월) + 릴리스 마커.
        // release-markers PUT 은 서버에서 ADMIN 만 허용한다(여기 edit 를 부여해도 MANAGER 는 쓰기 불가).
        key: 'refund-rate',
        label: '환불률',
        group: '커머스',
        navPath: '/v2/refund-rate',
        apiScopes: ['/api/admin/revenue/refund-rate', '/api/admin/revenue/release-markers'],
    },
    {
        key: 'products',
        label: '상품 관리',
        group: '운영',
        navPath: '/v2/products',
        apiScopes: [
            '/api/admin/products',
            '/api/admin/product-channel-codes',
            '/api/admin/smartstore/store-catalog',
            '/api/admin/smartstore/orders/lookup',
        ],
    },
    {
        key: 'roles',
        label: '권한 관리',
        group: '시스템',
        navPath: '/v2/roles',
        apiScopes: ['/api/admin/roles'],
        adminOnly: true,
    },
    {
        // /api/admin/settings 는 점검 모드·가입 차단까지 한 엔드포인트로 다루는 범용 키밸류라
        // 권한 그룹에 부여하면 의도보다 넓게 열린다. v1 Settings 와 같은 ADMIN 전용으로 둔다.
        key: 'settings',
        label: '설정',
        group: '시스템',
        navPath: '/v2/settings',
        apiScopes: ['/api/admin/settings'],
        adminOnly: true,
    },
    {
        // 매출 집계 제외 계정 (FIKET-485) — 관리자 테스트 결제를 매출·환불 집계에서 빼는 전역 설정. 쓰기는 서버가 ADMIN 만 허용.
        // GET 은 /api/admin/revenue 스코프(매출 관리)에도 걸려 MANAGER 가 "제외 중" 배지는 볼 수 있다.
        key: 'revenue-exclusions',
        label: '매출 제외 계정',
        group: '시스템',
        navPath: '/v2/settings/revenue-exclusions',
        apiScopes: ['/api/admin/revenue/excluded-users', '/api/admin/users'],
        adminOnly: true,
    },
    {
        // 지표 카탈로그 (FIKET-515) — 자연어 분석 대시보드용 소스·차원·지표 정의 관리. ADMIN 전용.
        key: 'metrics-catalog',
        label: '지표 정의',
        group: '분석',
        navPath: '/v2/analytics/metrics',
        apiScopes: ['/api/admin/metrics-catalog'],
        adminOnly: true,
    },
    {
        key: 'analytics-ask', label: '질문하기', group: '분석', navPath: '/v2/analytics/ask',
        apiScopes: ['/api/admin/metrics-catalog'], adminOnly: true,
    },
    {
        key: 'analytics-assistant', label: '분석 도우미', group: '분석', navPath: '/v2/analytics/assistant',
        apiScopes: ['/api/admin/analysis'], adminOnly: true,
    },
    {
        key: 'analytics-history', label: '대화 기록', group: '분석', navPath: '/v2/analytics/history',
        apiScopes: ['/api/admin/analysis'], adminOnly: true,
    },
    {
        key: 'analytics-boards', label: '내 대시보드', group: '분석', navPath: '/v2/analytics/boards',
        apiScopes: ['/api/admin/boards'], adminOnly: true,
    },
];
/** 권한 그룹에 부여할 수 있는 메뉴 키(adminOnly 제외) */
export const GRANTABLE_ADMIN_MENU_KEYS = ADMIN_MENUS.filter((m) => !('adminOnly' in m && m.adminOnly)).map((m) => m.key);
const MENU_BY_KEY = new Map(ADMIN_MENUS.map((m) => [m.key, m]));
export function getAdminMenu(key) {
    return MENU_BY_KEY.get(key);
}
export function isAdminMenuKey(key) {
    return MENU_BY_KEY.has(key);
}
export function pathMatchesScope(path, scope) {
    return path === scope || path.startsWith(scope + '/');
}
/** API 경로에 해당하는 메뉴 목록(공유 API 는 여러 개). 없으면 빈 배열 = 등록되지 않은 경로 */
export function menusForApiPath(path) {
    return ADMIN_MENUS.filter((m) => m.apiScopes.some((s) => pathMatchesScope(path, s)));
}
/** HTTP 메서드 → 필요한 액션. GET/HEAD = view, 그 외 전부 edit */
export function requiredActionForMethod(method) {
    const m = method.toUpperCase();
    return m === 'GET' || m === 'HEAD' ? 'view' : 'edit';
}
/**
 * grants 정규화 — 알 수 없는 메뉴키·adminOnly 메뉴·중복 제거, edit 가 있으면 view 를 포함시킨다.
 * DB jsonb 를 읽은 뒤(신뢰 안 함)와 요청 body 저장 직전에 호출.
 */
export function normalizeAdminMenuGrants(input) {
    const out = {};
    if (!input || typeof input !== 'object' || Array.isArray(input))
        return out;
    for (const [key, raw] of Object.entries(input)) {
        const menu = MENU_BY_KEY.get(key);
        if (!menu || menu.adminOnly || !Array.isArray(raw))
            continue;
        const actions = new Set();
        for (const a of raw) {
            if (a === 'view' || a === 'edit')
                actions.add(a);
        }
        if (actions.has('edit'))
            actions.add('view');
        if (actions.size === 0)
            continue;
        // 순서 고정(view, edit) — 비교·표시 안정성
        out[key] = ADMIN_MENU_ACTIONS.filter((a) => actions.has(a));
    }
    return out;
}
export function canViewAdminMenu(grants, key) {
    return !!grants && !!grants[key]?.includes('view');
}
export function canEditAdminMenu(grants, key) {
    return !!grants && !!grants[key]?.includes('edit');
}
/**
 * MANAGER 의 API 접근 판정. 경로에 매칭되는 메뉴 중 하나라도 필요한 액션을 부여받았으면 허용.
 * 매칭되는 메뉴가 없으면(미등록 경로) 거부.
 */
export function hasAdminApiAccess(grants, path, method) {
    const menus = menusForApiPath(path);
    if (menus.length === 0)
        return { allowed: false, reason: 'unregistered' };
    const action = requiredActionForMethod(method);
    const hasView = menus.some((m) => canViewAdminMenu(grants, m.key));
    if (action === 'view')
        return hasView ? { allowed: true } : { allowed: false, reason: 'no_view' };
    const hasEdit = menus.some((m) => canEditAdminMenu(grants, m.key));
    if (hasEdit)
        return { allowed: true };
    return { allowed: false, reason: hasView ? 'no_edit' : 'no_view' };
}
/** v2 브라우저 경로 → 메뉴 키(가장 긴 navPath prefix). '/v2'(홈) 등 메뉴가 아니면 null */
export function adminMenuKeyForNavPath(pathname) {
    let best = null;
    for (const m of ADMIN_MENUS) {
        if (pathMatchesScope(pathname, m.navPath) && (!best || m.navPath.length > best.navPath.length))
            best = m;
    }
    return best?.key ?? null;
}
//# sourceMappingURL=adminMenus.js.map